'use client'

import { LuneTheme } from '@lune-fe/lune-ui-lib'
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos'
import Box from '@mui/material/Box'
import Text from '@mui/material/Typography'
import Image, { StaticImageData } from 'next/image'
import Link from 'next/link'
import { FC } from 'react'

import LuneButton from '@/components/LuneButton'
import { RenderOn } from '@/utills/DeviceUtils'

export interface IllustratedTile {
    image: string | StaticImageData
    title: string
    description?: string
    href: string
    imageAlt: string
}

const Tile: FC<IllustratedTile> = ({ title, href, image, imageAlt, description }) => {
    const theme = LuneTheme

    return (
        <>
            <RenderOn breakPoints={['xs', 'sm', 'md']}>
                <Link
                    href={href}
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        textDecoration: 'none',
                        width: '100%',
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'start',
                            alignItems: 'center',
                        }}
                    >
                        <Box
                            sx={{
                                width: 87,
                                background: theme.palette.Grey100,
                                borderRadius: `8px`,
                                height: 60,
                                display: 'flex',
                                justifyContent: 'center',
                                position: 'relative',
                                overflow: 'hidden',
                            }}
                        >
                            <Image
                                style={{
                                    bottom: 0,
                                    position: 'absolute',
                                }}
                                width={80}
                                height={53.98}
                                src={image}
                                alt={imageAlt}
                            />
                        </Box>
                        <Box
                            sx={{
                                pl: 3,
                                pr: 3,
                                display: 'flex',
                                flexDirection: 'column',
                                width: 'calc(100% - 87px)',
                            }}
                        >
                            <Text
                                sx={{
                                    color: theme.palette.Grey900,
                                }}
                                variant={`button`}
                            >
                                {title}
                            </Text>
                            {description && (
                                <Text
                                    sx={{
                                        color: theme.palette.Grey900,
                                        mt: 1,
                                    }}
                                    variant={'body3'}
                                >
                                    {description}
                                </Text>
                            )}
                        </Box>
                    </Box>

                    <ArrowForwardIosIcon
                        sx={{
                            color: theme.palette.Grey900,
                            fontSize: 16,
                            fontWeight: `bold`,
                            mr: 1,
                        }}
                    />
                </Link>
            </RenderOn>
            <RenderOn breakPoints={['lg', 'xl']}>
                <Box
                    sx={{
                        position: 'relative',
                        display: 'flex',
                        justifyContent: 'center',
                        flexDirection: {
                            xs: 'column',
                            sm: 'column',
                            md: 'row',
                            lg: 'row',
                            xl: 'row',
                        },
                        height: description ? 284 : 192,
                        '> a': {
                            textDecoration: 'none !important',
                        },
                    }}
                >
                    <Link href={href}>
                        <Box
                            sx={{
                                width: 178,
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center',
                                '&:hover, &:focus, &:focus-within': {
                                    '.MuiSvgIcon-root': {
                                        transform: 'translateX(4px)',
                                    },
                                },
                            }}
                        >
                            <Box
                                sx={{
                                    width: 178,
                                    background: theme.palette.Grey100,
                                    borderRadius: `24px`,
                                    height: 120,
                                    display: 'flex',
                                    justifyContent: 'center',
                                    position: 'relative',
                                    overflow: 'hidden',
                                }}
                            >
                                <Image
                                    style={{
                                        bottom: 0,
                                        position: 'absolute',
                                    }}
                                    width={164}
                                    height={110.65}
                                    src={image}
                                    alt={imageAlt}
                                />
                            </Box>
                            {description && (
                                <Text
                                    sx={{
                                        mt: 3,
                                        color: `Grey900`,
                                    }}
                                    variant={'button'}
                                >
                                    {description}
                                </Text>
                            )}
                            <LuneButton
                                label={title}
                                sx={{
                                    position: 'absolute',
                                    bottom: 0,
                                }}
                            />
                        </Box>
                    </Link>
                </Box>
            </RenderOn>
        </>
    )
}

// Measured on the homepage row: three 178px tiles in the 900px container.
// Below lg the tiles stack and the rendered gap is the existing spacing value 2 (16px).
// At lg and xl, space-between produces 183px between tiles.
const homepageTileGap = {
    xs: '16px',
    sm: '16px',
    md: '16px',
    lg: '183px',
    xl: '183px',
} as const

const IllustratedMainLinks: FC<{ tiles: IllustratedTile[]; centered?: boolean }> = ({
    tiles,
    centered = false,
}) => (
    <Box
        sx={{
            // The 900px row is the three-tile measure. In the page column that width
            // stays left-aligned, so a centred pair inside it sits left of the page.
            // Hug the visible tiles and centre that pair. The default row is unchanged.
            width: centered
                ? { xs: '100%', sm: '100%', md: '100%', lg: 'fit-content', xl: 'fit-content' }
                : '100%',
            maxWidth: '900px',
            margin: 'auto',
            ...(centered
                ? {
                      alignSelf: { lg: 'center', xl: 'center' },
                  }
                : {}),
            display: 'flex',
            justifyContent: centered ? 'center' : 'space-between',
            my: 1,
            gap: centered
                ? homepageTileGap
                : {
                      xs: 2,
                      sm: 2,
                      md: 2,
                      lg: 0,
                      xl: 0,
                  },
            flexDirection: {
                xs: 'column',
                sm: 'column',
                md: 'column',
                lg: 'row',
                xl: 'row',
            },
        }}
    >
        {tiles.map((tile, index) => (
            <Tile
                key={index}
                image={tile.image}
                title={tile.title}
                href={tile.href}
                description={tile.description}
                imageAlt={tile.imageAlt}
            />
        ))}
    </Box>
)

export default IllustratedMainLinks
